import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import BackButton from "@/components/back-button";
import StarRating from "@/components/star-rating";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useMarkWorkerSeenMutation } from "@/queries/hooks/rosterHooks";

import { makeProfileQuery } from "./-queries";

import { useRole } from "@/utils/hooks/use-role";
import { AvailabilitySection } from "./-components/availability-section";
import { ExperienceRoles } from "./-components/experience-roles";
import { StarButton } from "./-components/star-button";

export const Route = createFileRoute("/app/profile/worker/$workerId/")({
  component: WorkerProfilePage,
});

export default function WorkerProfilePage() {
  const { t } = useTranslation("profile");
  const { workerId } = Route.useParams();
  const { data } = useSuspenseQuery(makeProfileQuery(workerId));
  const profile = data.profile;
  const shiftsCompleted = data.shifts_completed ?? null;
  const navigate = useNavigate();
  const role = useRole();
  const markSeen = useMarkWorkerSeenMutation();

  // When an employer views a worker's profile, record it so the worker's
  // "new_worker_signup" Needs-attention item is dismissed.
  useEffect(() => {
    if (role === "employer") markSeen.mutate(workerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, workerId]);

  return (
    <div className="w-full min-h-[calc(100vh-64px)] bg-fog flex flex-col gap-6 py-8 px-2 md:px-8">
      <div className="max-w-7xl mx-auto w-full">
        <div className="mb-6">
          <BackButton />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Card */}
          <div className="col-span-1">
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-sm border p-6 flex flex-col items-center gap-4">
                <div className="absolute top-4 right-4">
                  <StarButton
                    workerId={profile.user_id}
                    workerName={profile.full_name}
                  />
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className="w-16 h-16 rounded-full overflow-hidden border-1 border-ember">
                    <Avatar className="w-16 h-16">
                      <AvatarImage
                        src={profile.avatar_url}
                        alt={profile.full_name}
                      />
                      <AvatarFallback>
                        {profile.full_name ? profile.full_name[0] : "W"}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div className="text-xl font-semibold">
                    {profile.full_name ?? "—"}
                  </div>
                  <div className="text-xs text-slate">
                    {t("worker.activeSinceFallback")}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 w-full mt-2">
                  <div className="flex flex-col items-center bg-fog rounded-md py-2">
                    <div className="text-xs text-muted-foreground">
                      {t("worker.shiftsCompleted")}
                    </div>
                    <div className="font-bold">
                      {shiftsCompleted ?? "—"}
                    </div>
                  </div>
                  <div className="flex flex-col items-center bg-fog rounded-md py-2">
                    <div className="text-xs text-muted-foreground">
                      {t("worker.rating")}
                    </div>
                    <StarRating
                      value={profile.overall_rating}
                      count={profile.rating_count ?? profile.total_review}
                      size="sm"
                      showCount
                    />
                  </div>
                </div>
                <ExperienceRoles
                  workerId={profile.user_id}
                  roles={data.experience_roles}
                />
                <div className="w-full flex flex-col gap-2 mt-6">
                  <button
                    className="w-full h-11 rounded-[25px] font-semibold text-garnet border border-garnet bg-white hover:bg-fog transition"
                    onClick={() =>
                      navigate({
                        to: "/app/$role/messages",
                        params: { role: "employer" },
                        search: {
                          tab: undefined,
                          to: profile.user_id,
                          businessId: undefined,
                          selectedOfferId: undefined,
                        },
                      })
                    }
                  >
                    {t("worker.sendMessage")}
                  </button>
                  {/* Send Offer button for employers only */}
                  <SendOfferButton worker={profile} />
                </div>
              </div>
            </div>
          </div>
          {/* Right Card */}
          <div className="col-span-1 md:col-span-2">
            <div className="relative h-full">
              <div className="bg-white rounded-2xl shadow-sm border p-6 min-h-[500px] flex flex-col gap-6">
                <div>
                  <div className="text-lg font-semibold mb-1">
                    {t("worker.additionalExperience")}
                  </div>
                  <div className="text-sm text-muted-foreground mb-4">
                    {profile.about_me ?? t("worker.noExperience")}
                  </div>
                  <div className="flex flex-col gap-4">
                    {(() => {
                      const resumeHref =
                        profile.resume_signed_url || profile.resume_url;
                      return resumeHref ? (
                        <a
                          href={resumeHref}
                          target="_blank"
                          rel="noreferrer"
                          className="font-semibold text-garnet underline"
                        >
                          {t("worker.viewResume")}
                        </a>
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          {t("worker.noResumeUploaded")}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <div>
                  <div className="text-lg font-semibold mb-1 mt-4">
                    {t("worker.educationCerts")}
                  </div>
                  <div className="flex flex-col gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-ember inline-block" />
                        <span className="font-semibold">
                          {t("worker.miamiSchool")}
                        </span>
                      </div>
                      <ul className="list-disc ml-8 text-sm mt-1 text-muted-foreground">
                        <li>{t("worker.cert1")}</li>
                        <li>{t("worker.cert2")}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6">
          <AvailabilitySection windows={profile.posted_availability} />
        </div>
      </div>
    </div>
  );
}

function SendOfferButton({ worker }: { worker: any }) {
  const { t } = useTranslation("profile");
  const role = useRole();
  const navigate = useNavigate();
  if (role !== "employer") return null;
  return (
    <button
      className="w-full h-11 rounded-[25px] font-semibold text-white border border-ember bg-ember hover:bg-ember/90 transition mt-2"
      type="button"
      onClick={() =>
        navigate({ to: `/app/profile/worker/${worker.user_id}/send-offer` })
      }
    >
      {t("worker.sendOffer")}
    </button>
  );
}
